import { useCallback, useEffect, useState } from 'react';

import { App, Card } from 'antd';
import { useTranslation } from 'react-i18next';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import {
  Store,
  StoreListCounts,
  StoreListFilter,
  StoreListResponse,
} from 'models/Store';

import { StoreService } from 'services/StoreService';

import BulkUploadModal from './components/BulkUploadModal';
import StoreFilters from './components/StoreFilters';
import StoreFormModal from './components/StoreFormModal';
import StoreTable from './components/StoreTable';

import './Stores.scss';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const PAGE_SIZE_STORAGE_KEY = 'stores.pageSize';

const EMPTY_COUNTS: StoreListCounts = {
  all: 0,
  capital: 0,
  region: 0,
  vip: 0,
  inactive: 0,
};

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

function openStoreOnMap(store: Store) {
  if (
    typeof store.latitude === 'number' &&
    typeof store.longitude === 'number'
  ) {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      store.address
    )}`,
    '_blank',
    'noopener,noreferrer'
  );
}

function Stores() {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [filter, setFilter] = useState<StoreListFilter>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState<Store[]>([]);
  const [counts, setCounts] = useState<StoreListCounts>(EMPTY_COUNTS);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    const response = await StoreService.list({ term, filter, page, pageSize });
    setLoading(false);

    if (response.success) {
      const data = response.data as StoreListResponse;
      setDataSource(data.items);
      setCounts(data.counts);
      setTotal(data.total);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t('scenes.stores.scene.errors.fetch_list'),
        3.5
      );
    }
  }, [term, filter, page, pageSize, message, t]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleTermChange = (value: string) => {
    setTerm(value);
    setPage(1);
  };

  const handleFilterChange = (value: StoreListFilter) => {
    setFilter(value);
    setPage(1);
  };

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextPageSize));
      } catch {
        // ignore
      }
      return;
    }
    setPage(nextPage);
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingStore(null);
    setFormOpen(true);
  };

  const openEdit = (store: Store) => {
    setFormMode('edit');
    setEditingStore(store);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingStore(null);
  };

  const handleSaved = () => {
    closeForm();
    fetchStores();
  };

  const handleToggleVip = async (store: Store) => {
    const nextVip = !store.isVip;
    const response = await StoreService.setVip(store.id, nextVip);
    if (response.success) {
      message.success(
        t(
          nextVip
            ? 'scenes.stores.scene.messages.vip_added'
            : 'scenes.stores.scene.messages.vip_removed'
        ),
        3.5
      );
      fetchStores();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t('scenes.stores.scene.errors.update'),
        3.5
      );
    }
  };

  const handleToggleActive = async (store: Store) => {
    const nextActive = !store.isActive;
    const response = await StoreService.setActive(store.id, nextActive);
    if (response.success) {
      message.success(
        t(
          nextActive
            ? 'scenes.stores.scene.messages.activated'
            : 'scenes.stores.scene.messages.deactivated'
        ),
        3.5
      );
      fetchStores();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t('scenes.stores.scene.errors.update'),
        3.5
      );
    }
  };

  return (
    <Layout title="">
      <div className="stores-page">
        <StoreFilters
          term={term}
          filter={filter}
          counts={counts}
          loading={loading}
          onTermChange={handleTermChange}
          onFilterChange={handleFilterChange}
          onBulkUpload={() => setBulkOpen(true)}
          onCreate={openCreate}
        />

        <Card className="stores-page__table-card" bordered={false}>
          <StoreTable
            dataSource={dataSource}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPaginationChange={handlePaginationChange}
            onEdit={openEdit}
            onToggleVip={handleToggleVip}
            onToggleActive={handleToggleActive}
            onOpenMap={openStoreOnMap}
          />
        </Card>
      </div>

      <StoreFormModal
        isOpen={formOpen}
        mode={formMode}
        store={editingStore}
        onCancel={closeForm}
        onSaved={handleSaved}
      />

      <BulkUploadModal isOpen={bulkOpen} onCancel={() => setBulkOpen(false)} />
    </Layout>
  );
}

export default Stores;
